import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useWindowWidth from '../../hooks/useWindowWidth';
import logo from '../../assets/logo.svg';

const TOGGLE_BULLETS = {
  individuals: [
    'Free to start — 50 turns/day included, no credit card required',
    'Practice real conversations or run Lab research simulations',
    'Full acoustic perturbation & 3-layer evaluation engine included',
  ],
  teams: [
    'Multi-agent scenarios, full corpus export, and team accounts',
    'Built for SLP programs, research labs, and hospital systems',
    'Custom pricing, SSO, and dedicated support available',
  ],
};

const FAQS = [
  {
    q: 'Is V2VLab free to use?',
    a: 'Yes — the Standard plan is free forever, with 50 turns per day across Practice and Lab modes. No credit card required to get started.',
  },
  {
    q: "What's the difference between Practice and Lab mode?",
    a: 'Practice Mode is for real-time conversation practice. Lab Mode is for research — generate multi-agent simulations, apply acoustic perturbation, and evaluate how speech recognition systems perceive the result.',
  },
  {
    q: 'Can I use V2VLab for clinical or research publications?',
    a: "Yes. V2VLab's evaluation engine is built on citable, scientifically grounded methodology (WER/CER, MATTR, TF-IDF coherence, and more). See Pricing for plan details and our Terms of Use for research guidelines.",
  },
];

const FEATURES = [
  { icon: '🎙', title: 'Voice + STT Simulation', desc: 'Generate multi-agent conversations with realistic voice and see exactly how speech recognition perceives them.' },
  { icon: '🔊', title: 'Acoustic Perturbation', desc: 'Apply categorical or parametric acoustic parameters — tremor, spectral muffling, background noise, and more.' },
  { icon: '📊', title: 'Scientific Evaluation', desc: 'Three-layer engine: computational metrics, LLM-scored rubrics, and clinical-linguistic feature detection.' },
  { icon: '🗄', title: 'Corpus Export', desc: 'Export full audio + transcript datasets for your research, tagged, organized, and ready to archive.' },
];

const styles = {
  page: { fontFamily: "'Segoe UI', sans-serif", backgroundColor: '#fff', color: '#111827' },
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 5%', height: '64px', borderBottom: '1px solid #e5e7eb',
    position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 50,
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  navLink: { fontSize: '14px', fontWeight: '600', color: '#374151', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit' },
  btnOutline: {
    padding: '9px 20px', borderRadius: '8px', border: '1px solid #0052CC',
    backgroundColor: '#fff', color: '#0052CC', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  hero: {
    display: 'grid', gap: '48px', padding: '64px 5% 80px',
    maxWidth: '1280px', margin: '0 auto', alignItems: 'start',
  },
  eyebrow: {
    fontSize: '12px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px',
  },
  h1: { fontSize: '40px', fontWeight: '800', color: '#111827', lineHeight: '1.15', marginBottom: '20px' },
  subhead: { fontSize: '16px', color: '#4b5563', lineHeight: '1.7', marginBottom: '28px', maxWidth: '520px' },
  toggleRow: { display: 'flex', gap: '8px', marginBottom: '24px' },
  pill: {
    padding: '9px 20px', borderRadius: '999px', border: '2px solid #e5e7eb',
    backgroundColor: '#fff', color: '#374151', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  pillActive: { border: '2px solid #0052CC', backgroundColor: '#0052CC', color: '#fff' },
  bulletRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' },
  bulletArrow: { color: '#0052CC', fontWeight: '700', flexShrink: 0, marginTop: '2px' },
  bulletText: { fontSize: '15px', color: '#374151', lineHeight: '1.5' },

  formCard: {
    backgroundColor: '#fff', borderRadius: '20px', padding: '36px',
    border: '1px solid #e5e7eb', boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
  },
  formTitle: { fontSize: '22px', fontWeight: '800', color: '#111827', marginBottom: '6px' },
  formSub: { fontSize: '13px', color: '#6b7280', marginBottom: '20px' },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '12px',
    boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
  },
  submitBtn: {
    width: '100%', padding: '13px', borderRadius: '10px', border: 'none',
    backgroundColor: '#0052CC', color: '#fff', fontSize: '15px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  forgotLink: {
    display: 'block', textAlign: 'right', fontSize: '13px', color: '#0052CC',
    fontWeight: '600', cursor: 'pointer', marginBottom: '16px', background: 'none',
    border: 'none', fontFamily: 'inherit', width: '100%',
  },
  bottomRow: { textAlign: 'center', fontSize: '13px', color: '#6b7280', marginTop: '16px' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px',
  },
  success: {
    backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px',
  },
  backLink: {
    fontSize: '13px', color: '#0052CC', fontWeight: '600', cursor: 'pointer',
    background: 'none', border: 'none', fontFamily: 'inherit', marginBottom: '16px',
    display: 'flex', alignItems: 'center', gap: '4px',
  },

  section: { padding: '64px 5%', maxWidth: '1280px', margin: '0 auto' },
  sectionTitle: { fontSize: '26px', fontWeight: '800', color: '#111827', marginBottom: '32px', textAlign: 'center' },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' },
  featureCard: { padding: '24px', borderRadius: '14px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' },
  featureIcon: { fontSize: '28px', marginBottom: '12px' },
  featureTitle: { fontSize: '16px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  featureDesc: { fontSize: '13px', color: '#6b7280', lineHeight: '1.6' },

  faqItem: { marginBottom: '8px', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' },
  faqHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', cursor: 'pointer' },
  faqQ: { fontSize: '14px', fontWeight: '700', color: '#111827' },
  faqA: { fontSize: '13px', color: '#6b7280', lineHeight: '1.6', padding: '0 20px 18px' },

  footer: { borderTop: '1px solid #e5e7eb', padding: '32px 5%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  footerLinks: { display: 'flex', gap: '20px', flexWrap: 'wrap' },
  footerLink: { fontSize: '13px', color: '#6b7280', textDecoration: 'none', cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit' },
};

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.faqItem}>
      <div style={styles.faqHeader} onClick={() => setOpen(o => !o)}>
        <div style={styles.faqQ}>{faq.q}</div>
        <span style={{ fontSize: '14px', color: '#6b7280' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={styles.faqA}>{faq.a}</div>}
    </div>
  );
}

const AUTH_ERROR_MESSAGES = {
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/user-not-found': 'No account found with that email address.',
  'auth/wrong-password': 'Incorrect email or password. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Contact support if you believe this is an error.',
  'auth/too-many-requests': 'Too many failed attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/email-already-in-use': 'An account with that email already exists. Try logging in instead.',
  'auth/weak-password': 'Password must be at least 6 characters.',
};
function friendlyAuthError(err) {
  // Firebase surfaces codes either on err.code or embedded in the message string
  const code = err?.code
    || (typeof err?.message === 'string' && err.message.match(/\(([^)]+)\)/)?.[1])
    || '';
  return AUTH_ERROR_MESSAGES[code]
    || 'Unable to log in. Please check your details and try again.';
}

export default function Login() {
  const navigate = useNavigate();
  const { login, resetPassword } = useAuth();
  const width = useWindowWidth();
  const isMobile = width < 900;

  const [audience, setAudience] = useState('individuals');
  const [mode, setMode] = useState('login'); // 'login' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      {/* Nav */}
      <nav style={styles.nav}>
        <img
          src={logo}
          alt="V2VLab"
          style={{ height: '28px', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        />
        <div style={styles.navRight}>
          <button style={styles.navLink} onClick={() => navigate('/pricing')}>Pricing</button>
          <button style={styles.btnOutline} onClick={() => navigate('/signup')}>Sign Up</button>
        </div>
      </nav>

      {/* Hero — split screen, identical to homepage */}
      <div style={{ ...styles.hero, gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr' }}>
        {/* Left — value prop (same as homepage) */}
        <div>
          <div style={styles.eyebrow}>AI-Powered Conversational Simulation</div>
          <div style={styles.h1}>Test Conversations Before the Real World Does.</div>
          <div style={styles.subhead}>
            V2VLab is a voice-to-voice conversational simulation platform for speech-language research and clinical education.
            Generate multi-agent conversations, apply acoustic perturbation, and see exactly how speech recognition perceives disordered or degraded speech.
          </div>

          <div style={styles.toggleRow}>
            {[
              { id: 'individuals', label: 'Individuals' },
              { id: 'teams', label: 'Teams & Institutions' },
            ].map(t => (
              <button
                key={t.id}
                style={{ ...styles.pill, ...(audience === t.id ? styles.pillActive : {}) }}
                onClick={() => setAudience(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {TOGGLE_BULLETS[audience].map((b, i) => (
            <div key={i} style={styles.bulletRow}>
              <span style={styles.bulletArrow}>→</span>
              <span style={styles.bulletText}>{b}</span>
            </div>
          ))}
        </div>

        {/* Right — login / reset form (this is the only differing part) */}
        <div style={styles.formCard}>
          {mode === 'login' ? (
            <>
              <div style={styles.formTitle}>Log in to your account</div>
              <div style={styles.formSub}>Welcome back — enter your details below.</div>
              {error && <div style={styles.error}>{error}</div>}
              <form onSubmit={handleLogin}>
                <input
                  style={styles.input} type="email" placeholder="Email"
                  value={email} onChange={e => setEmail(e.target.value)} required
                />
                <input
                  style={styles.input} type="password" placeholder="Password"
                  value={password} onChange={e => setPassword(e.target.value)} required
                />
                <button
                  type="button"
                  style={styles.forgotLink}
                  onClick={() => { setMode('reset'); setError(''); setResetSent(false); }}
                >
                  Forgot password?
                </button>
                <button style={styles.submitBtn} type="submit" disabled={loading}>
                  {loading ? 'Logging in...' : 'Log In →'}
                </button>
              </form>
              <div style={styles.bottomRow}>
                Don't have an account?{' '}
                <span style={{ color: '#0052CC', fontWeight: '700', cursor: 'pointer' }} onClick={() => navigate('/signup')}>Sign Up</span>
              </div>
            </>
          ) : (
            <>
              <button
                style={styles.backLink}
                onClick={() => { setMode('login'); setError(''); setResetSent(false); }}
              >
                ← Back to Log In
              </button>
              <div style={styles.formTitle}>Reset your password</div>
              <div style={styles.formSub}>Enter your email and we'll send you a reset link.</div>
              {error && <div style={styles.error}>{error}</div>}
              {resetSent ? (
                <div style={styles.success}>
                  Reset email sent. Check your inbox for a link to reset your password.
                </div>
              ) : (
                <form onSubmit={handleReset}>
                  <input
                    style={styles.input} type="email" placeholder="Email"
                    value={email} onChange={e => setEmail(e.target.value)} required
                  />
                  <button style={styles.submitBtn} type="submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Reset Link →'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>

      {/* Feature highlights — identical to homepage */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>What you can do</div>
        <div style={styles.featureGrid}>
          {FEATURES.map(f => (
            <div key={f.title} style={styles.featureCard}>
              <div style={styles.featureIcon}>{f.icon}</div>
              <div style={styles.featureTitle}>{f.title}</div>
              <div style={styles.featureDesc}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ — identical to homepage */}
      <div style={{ ...styles.section, maxWidth: '680px' }}>
        <div style={styles.sectionTitle}>Frequently asked questions</div>
        {FAQS.map((faq, i) => <FaqItem key={i} faq={faq} />)}
      </div>

      {/* Footer — identical to homepage */}
      <div style={styles.footer}>
        <img src={logo} alt="V2VLab" style={{ height: '22px' }} />
        <div style={styles.footerLinks}>
          <button style={styles.footerLink} onClick={() => navigate('/pricing')}>Pricing</button>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>Terms of Use</a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>Privacy Policy</a>
          <button style={styles.footerLink} onClick={() => navigate('/signup')}>Sign Up</button>
        </div>
      </div>
    </div>
  );
}
